import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type ReceiptConfigScope = 'default' | 'customer' | 'location' | 'terminal-profile';

export type ReceiptType = 'sale' | 'refund' | 'void' | 'parked' | 'x_report' | 'z_report' | 'copy';

export type ReceiptConfigRow = {
  id: string;
  scopeType: ReceiptConfigScope;
  scopeId: string | null;
  receiptType: ReceiptType;
  version: number;
  jsonConfig: Record<string, unknown>;
  effectiveAt: string | null;
  createdBy: string | null;
  createdAt: string;
};

type ConfigQuery = {
  scopeType: ReceiptConfigScope;
  scopeId?: string | null;
  receiptType: ReceiptType;
};

function paramsFor(q: ConfigQuery): Record<string, string> {
  const out: Record<string, string> = {
    scopeType: q.scopeType,
    receiptType: q.receiptType,
  };
  if (q.scopeId !== undefined && q.scopeId !== null && q.scopeId.length > 0)
    out.scopeId = q.scopeId;
  return out;
}

const CURRENT_KEY = (q: ConfigQuery) =>
  ['receipt-config', 'current', q.scopeType, q.scopeId ?? '', q.receiptType] as const;

export function useReceiptConfigCurrent(q: ConfigQuery, enabled = true) {
  return useQuery({
    queryKey: CURRENT_KEY(q),
    enabled,
    queryFn: async (): Promise<ReceiptConfigRow | null> => {
      const res = await apiClient.get<ReceiptConfigRow | null>('/receipt-configs', {
        params: paramsFor(q),
      });
      return res.data;
    },
  });
}

export function usePutReceiptConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      scopeType: ReceiptConfigScope;
      scopeId?: string | null;
      receiptType: ReceiptType;
      jsonConfig: Record<string, unknown>;
    }): Promise<ReceiptConfigRow> => {
      const res = await apiClient.put<ReceiptConfigRow>('/receipt-configs', body);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: [
          'receipt-config',
          'current',
          vars.scopeType,
          vars.scopeId ?? '',
          vars.receiptType,
        ],
      });
    },
  });
}

export const SCOPE_LABEL: Record<ReceiptConfigScope, string> = {
  default: 'Default',
  customer: 'Customer',
  location: 'Location',
  'terminal-profile': 'Terminal profile',
};

export const RECEIPT_TYPE_LABEL: Record<ReceiptType, string> = {
  sale: 'Sale',
  refund: 'Refund',
  void: 'Void',
  parked: 'Parked',
  x_report: 'X-Report',
  z_report: 'Z-Report',
  copy: 'Copy',
};

/**
 * Catalogue keys for the two maps above. The maps stay as the baked English,
 * so a caller resolves a label as `t(SCOPE_LABEL_KEY[s], SCOPE_LABEL[s])`.
 * Each key is a literal because the parity guard reads source for literal keys.
 */
export const SCOPE_LABEL_KEY: Record<ReceiptConfigScope, string> = {
  default: 'receiptConfig.vocab.scopeDefault',
  customer: 'receiptConfig.vocab.scopeCustomer',
  location: 'receiptConfig.vocab.scopeLocation',
  'terminal-profile': 'receiptConfig.vocab.scopeTerminalProfile',
};

export const RECEIPT_TYPE_LABEL_KEY: Record<ReceiptType, string> = {
  sale: 'receiptConfig.vocab.typeSale',
  refund: 'receiptConfig.vocab.typeRefund',
  void: 'receiptConfig.vocab.typeVoid',
  parked: 'receiptConfig.vocab.typeParked',
  x_report: 'receiptConfig.vocab.typeXReport',
  z_report: 'receiptConfig.vocab.typeZReport',
  copy: 'receiptConfig.vocab.typeCopy',
};
